/**
 * Parse a date string as a local calendar date.
 * Handles both DATE columns and TIMESTAMPTZ-at-midnight-UTC.
 *
 * - Date-only: "2026-01-22" → local midnight
 * - Midnight UTC: "2026-01-22T00:00:00+00" or ".000Z" → local midnight
 * - Real timestamp: "2026-01-22T15:30:00+00" → actual moment (unchanged)
 *
 * This fixes the timezone bug where DATE columns or TIMESTAMPTZ values at
 * UTC midnight display as the previous day in Pacific timezone.
 */
export function parseAsLocalDate(dateString: string | null | undefined | Date | number): Date {
  // Handle null/undefined
  if (!dateString && dateString !== 0) {
return new Date(NaN);
}

  // Handle Date objects - return as-is
  if (dateString instanceof Date) {
return dateString;
}

  // Handle non-strings (numbers, objects, etc.) - attempt conversion
  if (typeof dateString !== 'string') {
    try {
      return new Date(dateString);
    } catch {
      return new Date(NaN);
    }
  }

  // Date-only string (from DATE columns): "2026-01-22"
  if (!dateString.includes('T')) {
    return new Date(`${dateString}T00:00:00`);
  }

  // TIMESTAMPTZ at UTC midnight - handles:
  // - "2026-01-22T00:00:00Z"
  // - "2026-01-22T00:00:00.000Z"
  // - "2026-01-22T00:00:00+00"
  // - "2026-01-22T00:00:00+00:00"
  // - "2026-01-22T00:00:00.000+00:00"
  const midnightPattern = /^(\d{4}-\d{2}-\d{2})T00:00:00(?:\.\d+)?(?:Z|[+-]00(?::00)?)$/;
  const match = dateString.match(midnightPattern);
  if (match) {
    return new Date(`${match[1]}T00:00:00`);
  }

  // Real timestamp with actual time - parse as-is
  return new Date(dateString);
}

/**
 * Get a YYYY-MM-DD string from a Date using the browser's local timezone.
 * Inverse of `parseAsLocalDate` — use this instead of
 * `new Date().toISOString().split('T')[0]` which silently converts to UTC
 * and can shift the date forward in western timezones (EST/CST/PST).
 *
 * @param date - Optional Date object. Defaults to `new Date()` (now).
 */
export function toLocalDateString(date?: Date): string {
  const d = date ?? new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export const BUSINESS_TIMEZONE = 'America/Los_Angeles';

const businessDateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: BUSINESS_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
});

/** Today's YYYY-MM-DD in the business timezone (America/Los_Angeles). */
export function getBusinessDateString(date: Date = new Date()): string {
  return businessDateFormatter.format(date);
}

/**
 * Add N calendar days to a YYYY-MM-DD string. Timezone-invariant: operates
 * on the calendar tuple via UTC arithmetic, which is DST-safe because UTC
 * has no DST. Use for calendar-day windows (e.g. "7 days from today") —
 * NOT `Date.now() + 7 * 86400000`, which drifts by a day across
 * spring-forward / fall-back.
 */
export function addCalendarDays(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/**
 * Whole calendar days between two YYYY-MM-DD strings (`to - from`). DST-safe
 * because it operates on the calendar tuple via UTC. Negative when `to`
 * precedes `from`. Use for "days overdue" and similar deltas instead of
 * `Math.floor((nowDate - dueDate) / 86400000)` on local-midnight Dates —
 * that pattern loses or gains a day across spring-forward / fall-back.
 */
export function diffCalendarDays(fromDateStr: string, toDateStr: string): number {
  const [fy, fm, fd] = fromDateStr.split('-').map(Number);
  const [ty, tm, td] = toDateStr.split('-').map(Number);
  const fromMs = Date.UTC(fy, fm - 1, fd);
  const toMs = Date.UTC(ty, tm - 1, td);
  return Math.round((toMs - fromMs) / 86400000);
}

const businessFullFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: BUSINESS_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23'
});

/**
 * UTC milliseconds of the moment when the Pacific wall-clock reads
 * 00:00:00 on the given YYYY-MM-DD. DST-aware: spring-forward and
 * fall-back days return the correct UTC instant even though their
 * Pacific calendar length is 23h or 25h.
 */
export function pacificMidnightAsUtcMs(pacificDateStr: string): number {
  const [y, m, d] = pacificDateStr.split('-').map(Number);
  // Candidate: treat the YYYY-MM-DD tuple as if it were UTC midnight,
  // then measure how far Pacific wall-clock differs at that instant.
  const candidate = Date.UTC(y, m - 1, d);
  const parts = businessFullFormatter.formatToParts(new Date(candidate));
  const pick = (t: string) => Number(parts.find(p => p.type === t)?.value ?? 0);
  const wallClockAsUtc = Date.UTC(
    pick('year'),
    pick('month') - 1,
    pick('day'),
    pick('hour'),
    pick('minute'),
    pick('second')
  );
  // Pacific wall-clock lags UTC (offset is negative). Adding the magnitude
  // back yields the UTC instant when Pacific reads the target midnight.
  return candidate - (wallClockAsUtc - candidate);
}

/**
 * Milliseconds until the next Pacific-midnight boundary (plus a 1s buffer so
 * the callback fires strictly *after* the day has rolled over). Use to drive
 * UI state that must re-classify at Pacific midnight — e.g., "overdue" badges
 * on a long-lived detail view.
 *
 * DST-correct: spring-forward Sundays are 23h Pacific-side and fall-back
 * Sundays are 25h. Naive `24h - wall_clock_since_midnight` math is wrong on
 * those days; this computes the actual UTC instant of the next Pacific
 * midnight and subtracts `now`.
 */
export function msUntilNextBusinessMidnight(now: Date = new Date()): number {
  const todayPacific = getBusinessDateString(now);
  const tomorrowPacific = addCalendarDays(todayPacific, 1);
  const targetUtcMs = pacificMidnightAsUtcMs(tomorrowPacific);
  return targetUtcMs - now.getTime() + 1000;
}

/** Format an ISO date string as MM/DD/YYYY, returning "--" for null/empty. */
export function formatDateDisplay(date: string | null | undefined): string {
  if (!date) {
return '--';
}
  const d = new Date(`${date}T00:00:00`);
  if (isNaN(d.getTime())) {
return '--';
}
  return d.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  });
}

export function formatDistanceToNow(date: Date, options?: { addSuffix?: boolean }): string {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (diffInSeconds < 60) {
    const text = 'just now';
    return options?.addSuffix ? text : text;
  }
  
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    const text = `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''}`;
    return options?.addSuffix ? `${text} ago` : text;
  }
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    const text = `${diffInHours} hour${diffInHours !== 1 ? 's' : ''}`;
    return options?.addSuffix ? `${text} ago` : text;
  }
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) {
    const text = `${diffInDays} day${diffInDays !== 1 ? 's' : ''}`;
    return options?.addSuffix ? `${text} ago` : text;
  }
  
  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    const text = `${diffInMonths} month${diffInMonths !== 1 ? 's' : ''}`;
    return options?.addSuffix ? `${text} ago` : text;
  }
  
  const diffInYears = Math.floor(diffInMonths / 12);
  const text = `${diffInYears} year${diffInYears !== 1 ? 's' : ''}`;
  return options?.addSuffix ? `${text} ago` : text;
}

/**
 * Format date to M/D/YY h:mm A format (e.g., "6/13/25 4:50 PM")
 */
export function formatDateTime(dateString?: string | Date | null): string {
  if (!dateString) {
return '-';
}

  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;

    // Get date components
    const month = date.getMonth() + 1; // 0-indexed
    const day = date.getDate();
    const year = date.getFullYear().toString().slice(-2); // Last 2 digits

    // Get time components
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';

    // Convert to 12-hour format
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12

    // Format the string
    const formattedDate = `${month}/${day}/${year}`;
    const formattedTime = `${hours}:${minutes.toString().padStart(2, '0')} ${ampm}`;

    return `${formattedDate} ${formattedTime}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '-';
  }
}

const businessDateTimeShortFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: BUSINESS_TIMEZONE,
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true
});

/**
 * Format an instant as Pacific "MMM d, h:mm a" (e.g., "Jun 5, 4:36 PM").
 * Timezone-pinned to the business timezone (America/Los_Angeles) so the
 * rendered day + time are stable regardless of the viewer's browser timezone.
 * Use for monthly rollups where the parent row is a whole month, so a bare
 * time would be ambiguous. Returns "-" for null/invalid input.
 */
export function formatBusinessDateTimeShort(input: string | Date | null | undefined): string {
  if (!input) {
    return '-';
  }
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) {
    return '-';
  }
  return businessDateTimeShortFormatter.format(d);
}

const businessDateShortFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: BUSINESS_TIMEZONE,
  month: '2-digit',
  day: '2-digit',
  year: '2-digit'
});

/**
 * Format an instant as Pacific "MM/dd/yy" (e.g., "06/05/26"). Timezone-pinned
 * to the business timezone. Returns "-" for null/invalid input.
 */
export function formatBusinessDate(input: string | Date | null | undefined): string {
  if (!input) {
    return '-';
  }
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) {
    return '-';
  }
  return businessDateShortFormatter.format(d);
}